import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Ticket, Plus, LogOut, Clock, ChevronRight, Circle,
  CheckCircle, Pause, Loader2, Search, User
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import MyDevicesCard from '../components/MyDevicesCard';

const STATUS_COLORS = {
  'Open': 'bg-blue-100 text-blue-700',
  'In Progress': 'bg-yellow-100 text-yellow-700',
  'Waiting': 'bg-orange-100 text-orange-700',
  'Resolved': 'bg-green-100 text-green-700',
  'Closed': 'bg-gray-100 text-gray-600',
};

const STATUS_ICONS = {
  'Open': <Circle className="h-3 w-3 text-blue-500" />,
  'In Progress': <Clock className="h-3 w-3 text-yellow-500" />,
  'Waiting': <Pause className="h-3 w-3 text-orange-500" />,
  'Resolved': <CheckCircle className="h-3 w-3 text-green-500" />,
  'Closed': <CheckCircle className="h-3 w-3 text-gray-500" />,
};

const PRIORITY_COLORS = {
  'Critical': 'bg-red-100 text-red-700',
  'High': 'bg-orange-100 text-orange-700',
  'Medium': 'bg-yellow-100 text-yellow-700',
  'Low': 'bg-slate-100 text-slate-600',
};

const formatDate = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return d; }
};

const PortalDashboard = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newTicket, setNewTicket] = useState({ subject: '', description: '', priority: 'Medium', category: '' });

  const portalUser = JSON.parse(localStorage.getItem('portal_user') || '{}');
  const token = localStorage.getItem('portal_token');

  useEffect(() => {
    if (!token) { navigate('/portal/login'); return; }
    fetchTickets();
  }, [page, statusFilter]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await api.portalGetTickets(params);
      setTickets(res.data.tickets || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('portal_token');
        localStorage.removeItem('portal_user');
        navigate('/portal/login');
      }
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_user');
    navigate('/portal/login');
  };

  const handleSubmitTicket = async () => {
    if (!newTicket.subject.trim() || !newTicket.description.trim()) {
      toast.error('Subject and description are required');
      return;
    }
    setSubmitting(true);
    try {
      await api.portalCreateTicket(newTicket);
      toast.success('Ticket submitted!');
      setShowNewTicket(false);
      setNewTicket({ subject: '', description: '', priority: 'Medium', category: '' });
      fetchTickets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const openCount = tickets.filter(t => !['Closed', 'Resolved'].includes(t.status)).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center">
              <Ticket className="h-4 w-4" />
            </div>
            <div>
              <h1 className="text-sm font-bold text-slate-900 leading-none">IT Support Portal</h1>
              <p className="text-xs text-slate-500 mt-0.5">{portalUser.full_name || portalUser.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setShowNewTicket(true)}
              size="sm"
              className="gap-1.5 bg-slate-900 hover:bg-slate-800"
              data-testid="portal-new-ticket-btn"
            >
              <Plus className="h-4 w-4" />
              New Request
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-500" data-testid="portal-logout-btn">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-slate-900">{total}</div>
              <div className="text-xs text-slate-500 mt-0.5">Total Tickets</div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">{openCount}</div>
              <div className="text-xs text-slate-500 mt-0.5">Active</div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-600">{total - openCount}</div>
              <div className="text-xs text-slate-500 mt-0.5">Resolved</div>
            </CardContent>
          </Card>
        </div>

        {/* My Devices (patch status from Action1) */}
        <MyDevicesCard
          authMethod={portalUser.auth_method}
          onRequestHelp={(device) => {
            setNewTicket({
              subject: `Help with updates on ${device.name}`,
              description: `My computer ${device.name} shows pending updates that aren't clearing.\n\n${(device.advice || []).join('\n')}`,
              priority: 'Medium',
              category: '',
            });
            setShowNewTicket(true);
          }}
        />

        {/* Filters */}
        <div className="flex items-center gap-3">
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="w-36 h-8 text-xs" data-testid="portal-status-filter">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="Open">Open</SelectItem>
              <SelectItem value="In Progress">In Progress</SelectItem>
              <SelectItem value="Waiting">Waiting</SelectItem>
              <SelectItem value="Resolved">Resolved</SelectItem>
              <SelectItem value="Closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchTickets} className="h-8 text-xs">Refresh</Button>
        </div>

        {/* Ticket List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : tickets.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="p-12 text-center">
              <Ticket className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No tickets yet</p>
              <Button
                size="sm"
                onClick={() => setShowNewTicket(true)}
                className="mt-3 gap-1.5 bg-slate-900 hover:bg-slate-800"
              >
                <Plus className="h-4 w-4" />
                Submit Your First Request
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-slate-200 overflow-hidden">
            <div className="divide-y divide-slate-100">
              {tickets.map((t) => (
                <div
                  key={t.ticket_id}
                  onClick={() => navigate(`/portal/tickets/${t.ticket_id}`)}
                  className="px-4 py-3 hover:bg-slate-50 cursor-pointer transition-colors flex items-center gap-3"
                  data-testid={`portal-ticket-${t.ticket_id}`}
                >
                  <div className="flex-shrink-0">
                    {STATUS_ICONS[t.status] || <Circle className="h-3 w-3 text-slate-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-slate-400">#{t.ticket_number}</span>
                      <span className="text-sm font-medium text-slate-900 truncate">{t.summary}</span>
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-2">
                      <span>{formatDate(t.created_on)}</span>
                      {t.assigned_to && (
                        <>
                          <span>&middot;</span>
                          <span className="flex items-center gap-1"><User className="h-3 w-3" />{t.assigned_to}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge className={`text-xs ${PRIORITY_COLORS[t.priority] || ''}`}>{t.priority}</Badge>
                    <Badge className={`text-xs ${STATUS_COLORS[t.status] || ''}`}>{t.status}</Badge>
                    <ChevronRight className="h-4 w-4 text-slate-300" />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Pagination */}
        {total > 20 && (
          <div className="flex items-center justify-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
            <span className="text-xs text-slate-500">Page {page} of {Math.ceil(total / 20)}</span>
            <Button variant="outline" size="sm" disabled={page * 20 >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        )}
      </main>

      {/* New Ticket Dialog */}
      <Dialog open={showNewTicket} onOpenChange={setShowNewTicket}>
        <DialogContent className="max-w-lg" data-testid="portal-new-ticket-dialog">
          <DialogHeader>
            <DialogTitle>Submit New Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Subject</label>
              <Input
                value={newTicket.subject}
                onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                placeholder="Brief description of your issue"
                data-testid="portal-ticket-subject"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Description</label>
              <Textarea
                value={newTicket.description}
                onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                placeholder="Please describe your issue in detail..."
                rows={5}
                data-testid="portal-ticket-description"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Priority</label>
                <Select value={newTicket.priority} onValueChange={(v) => setNewTicket({ ...newTicket, priority: v })}>
                  <SelectTrigger data-testid="portal-ticket-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Category</label>
                <Select value={newTicket.category || ''} onValueChange={(v) => setNewTicket({ ...newTicket, category: v })}>
                  <SelectTrigger data-testid="portal-ticket-category">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Hardware">Hardware</SelectItem>
                    <SelectItem value="Software">Software</SelectItem>
                    <SelectItem value="Network">Network</SelectItem>
                    <SelectItem value="Account Access">Account Access</SelectItem>
                    <SelectItem value="Email">Email</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewTicket(false)}>Cancel</Button>
            <Button
              onClick={handleSubmitTicket}
              disabled={submitting}
              className="gap-2 bg-slate-900 hover:bg-slate-800"
              data-testid="portal-submit-ticket-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {submitting ? 'Submitting...' : 'Submit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortalDashboard;
